import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Hammer, Upload, FileArchive, Loader2, ArrowRight, GitBranch, AlertCircle, Github, Search } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MultiSourceDropzone from "../components/MultiSourceDropzone";

export default function KailashNewProject() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("upload");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle");

  const [gh, setGh] = useState({ connected: false, loading: true, repos: [], filter: "", branch: "", selected: null });

  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);

  useEffect(() => {
    if (mode !== "github" || gh.repos.length > 0) return;
    setGh((g) => ({ ...g, loading: true }));
    api.get("/github/repos")
      .then(({ data }) => setGh((g) => ({ ...g, connected: true, loading: false, repos: data || [] })))
      .catch((e) => {
        const notConnected = e?.response?.data?.detail?.includes("not connected");
        setGh((g) => ({ ...g, connected: !notConnected, loading: false }));
      });
  }, [mode, gh.repos.length]);

  const onDrop = (e) => { e.preventDefault(); const dropped = Array.from(e.dataTransfer.files || []); if (dropped.length) setFiles(dropped); };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Project name must be at least 2 characters"); return; }
    if (files.length === 0) { toast.error("Add a file or a ZIP of your project"); return; }
    setBusy(true); setPhase("creating");
    try {
      const { data: project } = await api.post("/kailash/projects", { name: name.trim(), description: description.trim() });
      setPhase("uploading");
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const token = localStorage.getItem("dpa_token");
      const res = await fetch(`${API}/kailash/projects/${project.id}/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Upload failed"); }
      setPhase("analyzing");
      const analyzed = await res.json();
      toast.success(`Project ready — ${analyzed.understanding?.file_count || 0} files indexed`);
      nav(`/kailash/projects/${project.id}`);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setBusy(false); setPhase("idle");
    }
  };

  const importFromGitHub = async () => {
    if (!gh.selected) { toast.error("Pick a repo first"); return; }
    setBusy(true); setPhase("importing");
    try {
      const { data } = await api.post("/kailash/projects/import-github", {
        full_name: gh.selected,
        branch: gh.branch || undefined,
        project_name: name.trim() || undefined,
      });
      toast.success(`Imported · ${data.understanding?.file_count || 0} files indexed`);
      nav(`/kailash/projects/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
      setBusy(false); setPhase("idle");
    }
  };

  const filteredRepos = gh.repos.filter((r) => (r.full_name || "").toLowerCase().includes(gh.filter.toLowerCase()));

  return (
    <div className="container-narrow max-w-2xl py-10 md:py-14" data-testid="kailash-new-project">
      <div className="mb-6">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Hammer className="w-3 h-3" /> New Kailash project</div>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold">Bring us a project.</h1>
      </div>

      <div className="flex gap-2 mb-6" data-testid="kailash-mode-tabs">
        <button onClick={() => setMode("upload")} className={`btn-outline-ink text-sm ${mode === "upload" ? "!bg-ink !text-cream" : ""}`} data-testid="kailash-mode-upload">
          <Upload className="w-4 h-4" /> Upload files
        </button>
        <button onClick={() => setMode("github")} className={`btn-outline-ink text-sm ${mode === "github" ? "!bg-ink !text-cream" : ""}`} data-testid="kailash-mode-github">
          <Github className="w-4 h-4" /> Connect GitHub
        </button>
      </div>

      {mode === "upload" ? (
        <form onSubmit={submitUpload} className="space-y-5">
          <NameFields name={name} setName={setName} description={description} setDescription={setDescription} />
          <MultiSourceDropzone files={files} onFiles={setFiles} data-testid="kailash-dropzone" />
          <div className="p-3 rounded-xl bg-saffron/10 border border-saffron/30 text-xs text-ink2 inline-flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" />
            <span>We never index <code className="font-mono">.env</code>, <code>.pem</code>, or other secret files. Binaries and heavy assets are skipped automatically.</span>
          </div>
          <button className="btn-primary" disabled={busy} data-testid="kailash-submit-upload">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {phase}…</> : <>Analyze project <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </form>
      ) : (
        <div className="space-y-5">
          <NameFields name={name} setName={setName} description={description} setDescription={setDescription} />
          {gh.loading ? (
            <div className="p-8 text-center text-ink2"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
          ) : !gh.connected ? (
            <div className="p-6 rounded-2xl border-2 border-border text-center" data-testid="kailash-github-connect">
              <Github className="w-8 h-8 mx-auto mb-3" />
              <p className="text-sm text-ink2 mb-4">Connect your GitHub account to import any repository you can access.</p>
              <a href={`${API}/github/oauth/start`} className="btn-primary text-sm">
                <Github className="w-4 h-4" /> Connect GitHub
              </a>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink2" />
                <input value={gh.filter} onChange={(e) => setGh((g) => ({ ...g, filter: e.target.value }))} placeholder="Filter repos…" className="input pl-9" data-testid="kailash-repo-filter" />
              </div>
              <div className="max-h-80 overflow-y-auto space-y-1 rounded-xl border border-border" data-testid="kailash-repo-list">
                {filteredRepos.slice(0, 40).map((r) => (
                  <button
                    key={r.full_name}
                    onClick={() => setGh((g) => ({ ...g, selected: r.full_name, branch: r.default_branch || "" }))}
                    className={`w-full text-left px-3 py-2.5 hover:bg-paper transition-colors flex items-center gap-3 ${gh.selected === r.full_name ? "bg-paper" : ""}`}
                    data-testid={`kailash-repo-${r.full_name.replace(/\//g, "-")}`}
                  >
                    <GitBranch className="w-3.5 h-3.5 text-ink2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.full_name}</div>
                      {r.description && <div className="text-xs text-ink2 truncate">{r.description}</div>}
                    </div>
                    {r.private && <span className="text-[10px] uppercase tracking-widest font-bold text-marigold">Private</span>}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-ink2 mb-1">Branch (optional)</label>
                <input value={gh.branch} onChange={(e) => setGh((g) => ({ ...g, branch: e.target.value }))} placeholder="main" className="input" data-testid="kailash-branch-input" />
              </div>
              <button onClick={importFromGitHub} className="btn-primary" disabled={busy || !gh.selected} data-testid="kailash-submit-github">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <>Import repo <ArrowRight className="w-3.5 h-3.5" /></>}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NameFields({ name, setName, description, setDescription }) {
  return (
    <>
      <div>
        <label className="block text-xs uppercase tracking-widest font-bold text-ink2 mb-1">Project name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-linux-app" className="input" data-testid="kailash-name-input" required />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest font-bold text-ink2 mb-1">Description (optional)</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short summary" className="input" data-testid="kailash-desc-input" />
      </div>
    </>
  );
}
